import type {AddComponentTypeOptions, ComponentDefinitionDefined} from 'grapesjs';
import {safeElement} from './safe-element';

export interface BaseInputComponentArgs {
  inputFieldType: string;
  /** HTML name attribute. May contain `[]` (e.g. for custom-input arrays). */
  inputName: string;
  /** Marker attribute name, e.g. 'data-is-pmw-form-email-input'. Must be a valid HTML attr. */
  dataMarkerAttribute: string;
  displayName: string;
  placeholder: string;
  style: object;
  isComponentCheck: (el: Element) => boolean;
  additionalAttributes?: Record<string, string | number | boolean>;
}

export function createBaseInputComponent(args: BaseInputComponentArgs): AddComponentTypeOptions {
  const {inputFieldType, inputName, dataMarkerAttribute, displayName, placeholder, style, isComponentCheck, additionalAttributes} = args;
  return {
    extend: 'input',
    model: {
      defaults: {
        tagName: 'input',
        toolbar: [],
        highlightable: false,
        copyable: false,
        editable: false,
        hoverable: false,
        selectable: false,
        draggable: false,
        badgable: false,
        name: displayName,
        attributes: {
          type: 'text',
          name: inputName,
          customLabel: '',
          placeholder,
          [dataMarkerAttribute]: '1',
          'data-pmw-type': inputFieldType,
          id: inputFieldType,
          disabled: true,
          ...(additionalAttributes ?? {}),
        },
        style,
        traits: [{placeholder, type: 'text'}],
      },
    },
    isComponent: (el: Element): boolean | ComponentDefinitionDefined => {
      if (!safeElement(el)) {
        return false;
      }
      if (el.tagName === 'INPUT' && isComponentCheck(el)) {
        return {type: inputFieldType} as ComponentDefinitionDefined;
      }
      return false;
    },
  };
}

export interface BaseSelectComponentArgs {
  inputFieldType: string;
  inputName: string;
  dataMarkerAttribute: string;
  displayName: string;
  style: object;
  isComponentCheck: (el: Element) => boolean;
  defaultOptionsHtml: string;
}

export function createBaseSelectComponent(args: BaseSelectComponentArgs): AddComponentTypeOptions {
  const {inputFieldType, inputName, dataMarkerAttribute, displayName, style, isComponentCheck, defaultOptionsHtml} = args;
  return {
    extend: 'select',
    model: {
      defaults: {
        tagName: 'select',
        toolbar: [],
        highlightable: false,
        copyable: false,
        editable: false,
        hoverable: false,
        selectable: false,
        draggable: false,
        badgable: false,
        name: displayName,
        attributes: {
          name: inputName,
          customLabel: '',
          [dataMarkerAttribute]: '1',
          'data-pmw-type': inputFieldType,
          id: inputFieldType,
          disabled: true,
        },
        style,
        components: defaultOptionsHtml,
        traits: [{type: 'select'}],
      },
    },
    isComponent: (el: Element): boolean | ComponentDefinitionDefined => {
      if (!safeElement(el)) {
        return false;
      }
      if (el.tagName === 'SELECT' && isComponentCheck(el)) {
        return {type: inputFieldType} as ComponentDefinitionDefined;
      }
      return false;
    },
  };
}
